import React from 'react';
import { Markup } from 'interweave';
import { color } from '@strava/ui-tokens/js';
import {
  XAxis,
  YAxis,
  BarChart,
  Bar,
  CartesianGrid,
  ResponsiveContainer
} from 'recharts';
import Stat from '@strava/ui/Stat';
import Stats from '@strava/ui/Stats';
import { useLoggedOutAthletePageContext } from '../../state/LoggedOutAthletePageContext';

import styles from './MonthlyStats.module.scss';

const MonthlyStats = () => {
  const { t, state } = useLoggedOutAthletePageContext();
  const { athleteData, locale } = state;
  const { stats } = athleteData;
  const { monthlyTime, monthlyDistance, chartData } = stats;

  function getMonthFromIndex(i) {
    // By default JS converts the date to match
    // the timezone based on the user's browser
    // so here we are maintaining the UTC timezone
    // so that its consistent with the data from the server
    const date = new Date(i);
    const utcDate = new Date(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate()
    );

    return utcDate.toLocaleString(locale, { month: 'short' });
  }

  const barChartData =
    chartData &&
    chartData.map((i) => ({
      month: getMonthFromIndex(i.month),
      miles: i.miles
    }));

  const tickTextStyle = {
    fill: color.colorExtendedNeutralN2,
    fontSize: '11px'
  };

  const getPath = (x, y, width, height, radius) => {
    const adjRadius = height < radius * 2 ? 2 : radius;
    const widthAdj = width - adjRadius * 2;
    const heightAdj = height - adjRadius * 2;
    const xAdj = x + adjRadius;
    return `M${xAdj},${y}
      h${widthAdj}
      a${adjRadius},${adjRadius} 0 0 1 ${adjRadius},${adjRadius}
      v${heightAdj}
      a${adjRadius},${adjRadius} 0 0 1 ${-adjRadius},${adjRadius}
      h${-widthAdj}
      a${adjRadius},${adjRadius} 0 0 1 ${-adjRadius},${-adjRadius}
      v${-heightAdj}
      a${adjRadius},${adjRadius} 0 0 1 ${adjRadius},${-adjRadius}
      z`;
  };

  const RoundedBar = (args) => {
    const { x, y, width, height, radius, fill } = args;
    return (
      <path
        d={getPath(x, y, width, height, radius)}
        stroke="none"
        fill={fill}
      />
    );
  };

  return (
    <div className={styles.monthlyStats} data-testid="monthly-stats">
      <h3>{t('current_month')}</h3>

      <Stats listClassName={styles.currMonthStats}>
        <Stat value={monthlyDistance} label={t('distance')} type="stat-lg" />
        <Stat
          value={<Markup noWrap={true} content={monthlyTime} />}
          label={t('moving_time')}
          type="stat-lg"
        />
      </Stats>

      {chartData && (
        <div className={styles.barChart}>
          <ResponsiveContainer height={225}>
            <BarChart data={barChartData} barGap={12}>
              <Bar
                dataKey="miles"
                fill={color.colorExtendedNeutralN6}
                minPointSize={4}
                shape={<RoundedBar />}
                radius={4}
              />
              <CartesianGrid
                vertical={false}
                strokeDasharray="2 3"
                x={0}
                width={800}
              />
              <XAxis
                dataKey="month"
                tick={tickTextStyle}
                stroke="none"
                minTickGap={0}
              />
              <YAxis
                dataKey="miles"
                tick={{ ...tickTextStyle, textAnchor: 'start' }}
                tickCount={3}
                stroke="none"
                width={20}
                dy={10}
                dx={-17}
                textAnchor="end"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default MonthlyStats;
